import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { RelatedArticleTeasersType } from "@swo/storefront";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { CSS_MQ } from "../../styles/breakpoints";
import { ImageWithFocalPoint } from "../_shared-components/image-related/ImageWithFocalPoint";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { Link } from "react-router";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { T } from "../T";

const Teaser: ReactTFC<RelatedArticleTeasersType["teasers"][0]> = props => {
  return (
    <Link
      to={{ pathname: props.link }}
      state={{ scrollToTop: true }}
      css={css`
        display: grid;
        grid-template-columns: 100px auto;

        margin: 18px 0;
        word-break: break-word;

        text-decoration: none;

        &:hover {
          h6 {
            text-decoration: underline;
          }
        }
      `}
    >
      <div
        css={css`
          position: relative;

          height: 100px;
        `}
      >
        <ImageWithFocalPoint {...props.image} requestedWidth={200} assetId={props.image.image.sys.id} />
      </div>
      <div
        css={css`
          margin-left: 16px;
          margin-right: 16px;
        `}
      >
        <span
          css={[
            FONTS.SwarovskiFutura500,
            fontColor.black,
            fontSize.x2,
            css`
              display: block;

              margin-top: 0;
              margin-bottom: 0;

              text-transform: uppercase;
            `,
          ]}
        >
          <EXPERIMENTAL_TextWithProductNameRenderer text={props.descriptiveHeadline} />
        </span>
        <span
          css={[
            FONTS.MinervaModern500,
            fontSize.x25,
            css`
              display: block;

              margin-top: 0;
              margin-bottom: 5px;

              color: ${CSS_COLORS.Green};

              text-transform: uppercase;
            `,
          ]}
        >
          {props.emoHeadline}
        </span>
        <span css={[FONTS.SwarovskiFutura300, fontSize.x2, fontColor.black]}>
          <T>{props.readingTime}</T>
        </span>
      </div>
    </Link>
  );
};

export const RelatedArticleTeasersComponent: ReactTFC<
  RelatedArticleTeasersType & ContentfulComponentCMSHook
> = props => {
  return (
    <ContentContainer attachStyles={containerStyles} className={getComponentCMSClassname(props?.sys?.id)}>
      <div
        className={`count-${props.teasers.length}`}
        css={css`
          display: grid;

          border-top: 1px solid ${CSS_COLORS.Grey2};
          border-bottom: 1px solid ${CSS_COLORS.Grey2};

          ${CSS_MQ.atLeastLg} {
            grid-template-columns: repeat(${props.teasers.length}, 1fr);
            grid-gap: 5px;
          }

          &.count-1 {
            ${CSS_MQ.atLeastLg} {
              max-width: calc(100% / 2);
            }
            ${CSS_MQ.atLeastXl} {
              max-width: calc(100% / 2.5);
            }
          }

          &.count-2 {
            ${CSS_MQ.atLeastLg} {
              max-width: calc(100%);
            }
            ${CSS_MQ.atLeastXl} {
              max-width: calc(100% / 1.5);
            }
          }
        `}
      >
        {props.teasers.map(teaser => (
          <Teaser key={teaser.link} {...teaser} />
        ))}
      </div>
    </ContentContainer>
  );
};

const containerStyles = css`
  ${CSS_MQ.atLeastLg} {
    display: flex;
    justify-content: center;
  }
`;
